// CSV for exports the advisor opens in a spreadsheet. Cells that a spreadsheet would run as a formula
// (=, +, -, @, tab, CR) are prefixed with an apostrophe, so an exported value can never execute.

export function csvCell(x: unknown): string {
  const s = x == null ? '' : String(x);
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",;\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** rows → CSV text (comma separated, CRLF line ends, no BOM — add one when saving for Excel) */
export function toCsv(rows: unknown[][]): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}

type AuditRow = { at: string; actor?: string | null; action: string; summary?: string | null; entity?: string | null; entityId?: string | null; data?: unknown };

/** the local audit trail (who · what · when · previous → new) as CSV, oldest first */
export function auditTrailCsv(rows: readonly AuditRow[], labels: Record<'at' | 'actor' | 'action' | 'summary' | 'entity' | 'changes', string>): string {
  const changes = (d: unknown) => (((d as { changes?: { t: string; id: string; f: Record<string, unknown> }[] } | null)?.changes) ?? [])
    .map((c) => `${c.t}/${c.id}: ${Object.entries(c.f).map(([f, v]) => (Array.isArray(v) ? `${f} ${String(v[0] ?? '—')} → ${String(v[1] ?? '—')}` : f)).join('; ')}`).join(' | ');
  const sorted = [...rows].sort((a, b) => a.at.localeCompare(b.at));
  return toCsv([[labels.at, labels.actor, labels.action, labels.summary, labels.entity, labels.changes], ...sorted.map((e) => [e.at, e.actor ?? '', e.action, e.summary ?? '', [e.entity, e.entityId].filter(Boolean).join('/'), changes(e.data)])]);
}

/** the team's platform activity log as CSV, oldest first */
export function activityCsv(rows: readonly { at: string; userId: string; email?: string | null; action: string; target?: string | null }[], labels: Record<'at' | 'member' | 'action' | 'target', string>, actionLabel: (a: string) => string = (a) => a): string {
  const sorted = [...rows].sort((a, b) => a.at.localeCompare(b.at));
  return toCsv([[labels.at, labels.member, labels.action, labels.target], ...sorted.map((r) => [r.at, r.email ?? r.userId, actionLabel(r.action), r.target ?? ''])]);
}
